<script setup lang="ts">
import { ref } from "vue";
import { RuneOfTheDayResponse } from "@/types/calculators";
import { fetchDayRunePredictions } from "@/api/calculators";
import BackButton from "@/components/buttons/BackButton.vue";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { useI18n } from "vue-i18n";
import DayRuneResult from "@/views/Calculators/DayRunePrediction/components/DayRuneResult.vue";
import DayRuneInstruction from "@/views/Calculators/DayRunePrediction/components/DayRuneInstruction.vue";
import { useSafeBack } from "@/utils/useSafeBack";
import { ROUTE_NAMES } from "@/constants/routeNames";

const { t } = useI18n();
const { safeBack } = useSafeBack();

const dailyRandom = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const runePrediction = ref<RuneOfTheDayResponse | null>(null);

async function handleSubmit() {
  if (isLoading.value) {
    return;
  }
  isLoading.value = true;
  try {
    runePrediction.value = await fetchDayRunePredictions(dailyRandom.value);
  } finally {
    isLoading.value = false;
  }
}

function handleBack() {
  if (runePrediction.value) {
    runePrediction.value = null;
    return;
  }
  safeBack(ROUTE_NAMES.HOME);
}
</script>

<template>
  <div class="flex flex-col h-full w-full px-4 md:mx-0 pt-6 xl:pt-[88px] pb-6">
    <div class="relative items-center w-full min-h-8 mb-6 md:hidden flex">
      <div class="absolute left-0">
        <BackButton @clicked="handleBack" />
      </div>
      <h2 class="mx-auto text-2xl font-semibold leading-[32px] text-brown">
        {{ t("calculators.day-rune.title") }}
      </h2>
    </div>
    <div
      class="text-left bg-white rounded-[20px] pb-6 pt-6 md:pt-[30px] flex flex-col items-center md:mx-auto w-full lg:w-[812px] h-full overflow-y-auto px-4 md:px-10"
    >
      <div class="relative items-center w-full mb-10 hidden md:flex">
        <div class="absolute left-0">
          <BackButton @clicked="handleBack" />
        </div>
        <h2 class="mx-auto text-2xl font-semibold leading-[32px] text-brown">
          {{ t("calculators.day-rune.title") }}
        </h2>
      </div>
      <GradientButton
        v-if="!runePrediction"
        class="w-[245px] min-h-14 mb-10"
        bg-color="bg-brown-gradient"
        hover-bg-color="bg-orange-gradient"
        :text="t('calculators.day-rune.get_rune_btn')"
        @click="handleSubmit"
        :is-loading="isLoading"
      />
      <DayRuneResult v-else :prediction="runePrediction" />
      <DayRuneInstruction />
    </div>
  </div>
</template>
